import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, DatePicker, AutoComplete, Button, Spin, Input, message, Alert, Typography } from 'antd';
import dayjs from 'dayjs';
import api from '../../services/api';
import { FindVesselModalProps, Port, SailingResult, SearchParams } from './types';
import { SailingResultsTable } from './SailingResultsTable';

const { Option } = Select;
const { Title } = Typography;

const FindVesselModal: React.FC<FindVesselModalProps> = ({
  open,
  onClose,
  shipmentId,
  initialOrigin,
  initialDestination,
  initialDate,
  initialWeeks,
  existingBooking,
  onBookingCreated
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SailingResult[] | null>(null);
  const [originOptions, setOriginOptions] = useState<{ value: string; label: string }[]>([]);
  const [destOptions, setDestOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedSailing, setSelectedSailing] = useState<SailingResult | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [imoInput, setImoInput] = useState('');

  // Initialize form values
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        origin: initialOrigin || '',
        destination: initialDestination || '',
        fromDate: initialDate ? dayjs(initialDate) : dayjs(),
        weeks: initialWeeks || 4,
        cargoType: 'GP'
      });
      setResults(null);
      setSelectedSailing(null);
    }
  }, [open, initialOrigin, initialDestination, initialDate, initialWeeks, form]);

  // Port Autocomplete
  const handlePortSearch = useCallback(async (value: string, setOptions: React.Dispatch<React.SetStateAction<{ value: string; label: string }[]>>) => {
    if (!value || value.length < 2) {
      setOptions([]);
      return;
    }

    try {
      const response = await api.get(`/ports/autocomplete?search=${encodeURIComponent(value)}`) as { ports: Port[] };
      const options = response.ports.map((port: Port) => ({
        value: port.code,
        label: `${port.code} - ${port.name} (${port.country})`
      }));
      setOptions(options);
    } catch (error) {
      console.error('Failed to fetch ports:', error);
    }
  }, []);

  // Debounce wrapper for port search
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const debouncedOriginSearch = useCallback(debounce((val: string) => handlePortSearch(val, setOriginOptions), 300), [handlePortSearch]);
  const debouncedDestSearch = useCallback(debounce((val: string) => handlePortSearch(val, setDestOptions), 300), [handlePortSearch]);

  // Search Sailings
  const handleSearch = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      setResults(null);

      const fromDate = values.fromDate.format('YYYY-MM-DD');
      const toDate = values.fromDate.add(values.weeks, 'week').format('YYYY-MM-DD');

      const params: SearchParams = {
        origin: values.origin,
        destination: values.destination,
        fromDate,
        toDate,
        cargoType: values.cargoType
      };

      const response = await api.get('/one-schedules/search', { params }) as { sailings: SailingResult[] };
      setResults(response.sailings);
      
      if (response.sailings.length === 0) {
        message.info('No sailings found for the selected criteria.');
      }
    } catch (error) {
      console.error('Search failed:', error);
      message.error('Failed to search sailings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Selection
  const handleSelectSailing = (sailing: SailingResult) => {
    setSelectedSailing(sailing);
    setImoInput(''); // Reset IMO input
    setConfirmModalOpen(true);
  };

  // Confirm Selection
  const handleConfirmSelection = async () => {
    if (!selectedSailing) return;

    try {
      setConfirmLoading(true);

      // Prepare payload
      const payload = {
        shipmentId,
        bookingId: existingBooking?.id,
        vvd: selectedSailing.vvd,
        vesselName: selectedSailing.vesselName,
        vesselCode: selectedSailing.vesselCode,
        etd: selectedSailing.etd,
        eta: selectedSailing.eta,
        cyCut: selectedSailing.cyCut,
        docsCut: selectedSailing.docsCut,
        carrier: 'ONEY',
        vesselImo: imoInput || undefined,
        originPort: selectedSailing.originPort || undefined,
        destinationPort: selectedSailing.destinationPort || undefined,
        transshipmentPort: selectedSailing.transshipments?.[0]?.portCode || selectedSailing.transshipments?.[0]?.port || undefined,
      };

      await api.post('/one-schedules/select', payload);

      const action = existingBooking?.id ? 'updated' : 'created';
      message.success(`Booking ${action} successfully!`);
      setConfirmModalOpen(false);
      onClose();
      if (onBookingCreated) {
        onBookingCreated();
      }
    } catch (error) {
      console.error('Booking failed:', error);
      message.error('Failed to create/update booking. Please try again.');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <>
      <Modal
        title="Find Vessel (ONE Line)"
        open={open}
        onCancel={onClose}
        width={1200}
        footer={null}
        destroyOnClose
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {existingBooking && (
          <Alert
            message={existingBooking.id ? "Update Existing Booking" : "Warning: Shipment has existing booking"}
            description={
              <div>
                {existingBooking.id ? (
                  <>
                    This shipment has a booking that will be updated with the selected vessel/voyage information.<br />
                    {existingBooking.vessel && <span>Current Vessel/Voyage: <strong>{existingBooking.vessel} {existingBooking.voyage}</strong><br /></span>}
                    {existingBooking.bookingNumber && <span>Booking Number: <strong>{existingBooking.bookingNumber}</strong><br /></span>}
                  </>
                ) : (
                  <>
                    This shipment already has a booking assigned.<br />
                    {existingBooking.vessel && <span>Vessel/Voyage: <strong>{existingBooking.vessel} {existingBooking.voyage}</strong><br /></span>}
                    {existingBooking.bookingNumber && <span>Booking Number: <strong>{existingBooking.bookingNumber}</strong><br /></span>}
                    Creating a new booking will not automatically remove the existing one.
                  </>
                )}
              </div>
            }
            type={existingBooking.id ? "info" : "error"}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
          initialValues={{ weeks: 4, cargoType: 'GP' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="origin"
              label="Origin Port"
              rules={[{ required: true, message: 'Please select origin port' }]}
            >
              <AutoComplete
                options={originOptions}
                onSearch={debouncedOriginSearch}
                placeholder="e.g., USLAX"
              />
            </Form.Item>

            <Form.Item
              name="destination"
              label="Destination Port"
              rules={[{ required: true, message: 'Please select destination port' }]}
            >
              <AutoComplete
                options={destOptions}
                onSearch={debouncedDestSearch}
                placeholder="e.g., JPYOK"
              />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <Form.Item
              name="fromDate"
              label="Start Date"
              rules={[{ required: true, message: 'Please select start date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="weeks"
              label="Search Range"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value={2}>2 Weeks</Option>
                <Option value={4}>4 Weeks</Option>
                <Option value={6}>6 Weeks</Option>
                <Option value={8}>8 Weeks</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="cargoType"
              label="Cargo Type"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="GP">General Purpose (Dry)</Option>
                <Option value="RF">Reefer</Option>
              </Select>
            </Form.Item>
          </div>

          <div style={{ textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" loading={loading} icon={<span role="img" aria-label="search">🔍</span>}>
              Search Sailings
            </Button>
          </div>
        </Form>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="Searching ONE Line schedules..." />
          </div>
        )}

        {results && (
          <div style={{ marginTop: 24 }}>
            <Title level={5} style={{ marginBottom: 16 }}>Available Sailings ({results.length})</Title>
            <SailingResultsTable results={results} onSelect={handleSelectSailing} />
          </div>
        )}
      </Modal>

      {/* Confirmation Sub-Modal */}
      <Modal
        title="Confirm Vessel Selection"
        open={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onOk={handleConfirmSelection}
        confirmLoading={confirmLoading}
        okText={existingBooking?.id ? "Update Booking" : "Create Booking"}
      >
        {selectedSailing && (
          <div>
            <Alert
              message={existingBooking?.id ? "Update Booking Details" : "Confirm Booking Details"}
              description={existingBooking?.id
                ? "This will update the existing booking with the selected vessel/voyage information."
                : "This will create a new booking record and assign it to the shipment."}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div><strong>Vessel:</strong> {selectedSailing.vesselName}</div>
              <div><strong>Voyage:</strong> {selectedSailing.vvd}</div>
              <div><strong>ETD:</strong> {dayjs(selectedSailing.etd).format('MMM D, YYYY')}</div>
              <div><strong>ETA:</strong> {dayjs(selectedSailing.eta).format('MMM D, YYYY')}</div>
              <div><strong>CY Cut:</strong> {selectedSailing.cyCut ? dayjs(selectedSailing.cyCut).format('MMM D, HH:mm') : 'N/A'}</div>
              <div><strong>Transit:</strong> {selectedSailing.transitTime} days</div>
            </div>

            <Form layout="vertical">
              <Form.Item label="Vessel IMO (Optional)">
                <Input 
                  placeholder="e.g., 9806043" 
                  value={imoInput}
                  onChange={(e) => setImoInput(e.target.value)}
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Look up IMO at <a href="https://www.marinetraffic.com" target="_blank" rel="noopener noreferrer">MarineTraffic.com</a>
                </div>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </>
  );
};

export default FindVesselModal;
